import { z } from 'zod';
import { uploadedFileSchema } from '@terra/shared-authorization';
import { OptionalBooleanQueryParamSchema } from '@terra/shared-dto-types';
import {
  SignalStageSchema,
  SignalStatusSchema,
} from '@terra/shared-schemas-orchestrator';

export const FEEDBACK_SCREENSHOT_MAX_SIZE_BYTES = 5 * 1024 * 1024;
export const FEEDBACK_SCREENSHOT_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
] as const;

export const CategoryClassificationPassSchema = z.enum([
  'feedback',
  'trace_augmented',
]);

export type CategoryClassificationPass = z.infer<
  typeof CategoryClassificationPassSchema
>;

export const FeedbackFailureCategorySchema = z.enum([
  'missing_attack_step',
  'playbook_classification',
  'endpoint_behavior',
  'application_behavior',
  'not_enough_info',
  'environmental_stale_endpoint',
  'environmental_neo_tooling',
  'environmental_test_setup',
  'environmental_cant_replicate',
]);

export type FeedbackFailureCategory = z.infer<
  typeof FeedbackFailureCategorySchema
>;

export const FeedbackFailureAreaSchema = z.enum([
  'signal_generation',
  'attack_execution',
  'post_attack_analysis',
]);

export type FeedbackFailureArea = z.infer<typeof FeedbackFailureAreaSchema>;

export const FEEDBACK_FAILURE_AREA_ORDER: FeedbackFailureArea[] =
  FeedbackFailureAreaSchema.options;

export const FEEDBACK_FAILURE_CATEGORY_ORDER: FeedbackFailureCategory[] =
  FeedbackFailureCategorySchema.options;

export const FEEDBACK_FAILURE_CATEGORY_LABELS: Record<
  FeedbackFailureCategory,
  string
> = {
  missing_attack_step: 'Missing Attack Step',
  playbook_classification: 'Playbook Classification',
  endpoint_behavior: 'Endpoint Behavior',
  application_behavior: 'Application Behavior',
  not_enough_info: 'Not Enough Info',
  environmental_stale_endpoint: 'Environmental Stale Endpoint',
  environmental_neo_tooling: 'Environmental Neo Tooling',
  environmental_test_setup: 'Environmental Test Setup',
  environmental_cant_replicate: "Environmental Can't Replicate",
};

export const isFeedbackFailureCategory = (
  value: string,
): value is FeedbackFailureCategory =>
  FeedbackFailureCategorySchema.safeParse(value).success;

export const formatFeedbackFailureCategoryLabel = (
  snakeCase: string,
): string => {
  if (isFeedbackFailureCategory(snakeCase)) {
    return FEEDBACK_FAILURE_CATEGORY_LABELS[snakeCase];
  }

  return snakeCase
    .split('_')
    .filter((part) => part.length > 0)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
};

export const ApplicationBehaviorTypeSchema = z.enum(['authorization', 'other']);

export type ApplicationBehaviorType = z.infer<
  typeof ApplicationBehaviorTypeSchema
>;

export const ContinualLearningRoutedActionValues = [
  'memory',
  'linear',
  'slack',
  'retest',
  'investigation',
  'notification',
  'none',
] as const;

export type ContinualLearningRoutedAction =
  (typeof ContinualLearningRoutedActionValues)[number];

export const ContinualLearningRoutedActionSchema = z.preprocess(
  (value) => (value === 'ticket_queue' ? 'linear' : value),
  z.enum(ContinualLearningRoutedActionValues).optional(),
);

const nullableSignalStatusFromMultipartField = z.preprocess(
  (val) => (val === undefined || val === '' ? null : val),
  SignalStatusSchema.nullable(),
);

export const SubmitSignalFeedbackRequestSchema = z.object({
  applicationId: z.string(),
  signalId: z.string(),
  previousStage: SignalStageSchema,
  previousStatus: nullableSignalStatusFromMultipartField,
  newStage: SignalStageSchema,
  newStatus: nullableSignalStatusFromMultipartField,
  rating: z.enum(['positive', 'negative']).optional(),
  feedback: z.string().max(2000).optional(),
  failureCategory: FeedbackFailureCategorySchema.optional(),
  failureArea: FeedbackFailureAreaSchema.optional(),
  screenshotPath: z.string().optional(),
  screenshot: uploadedFileSchema({
    mimeTypes: FEEDBACK_SCREENSHOT_MIME_TYPES,
    maxFileSizeBytes: FEEDBACK_SCREENSHOT_MAX_SIZE_BYTES,
  }).optional(),
  isFalsePositive: OptionalBooleanQueryParamSchema,
});

export const SubmitSignalFeedbackSuccessResponseSchema = z.object({
  success: z.literal(true),
});

export const SubmitSignalFeedbackErrorResponseSchema = z.object({
  success: z.literal(false),
  error: z.string(),
  details: z.array(z.any()).optional(),
});

export const SubmitSignalFeedbackResponseSchema = z.union([
  SubmitSignalFeedbackSuccessResponseSchema,
  SubmitSignalFeedbackErrorResponseSchema,
]);
